import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Hammer,
  ShieldCheck,
  BrainCircuit,
  UsersRound,
} from 'lucide-react'
import { Container } from '../layout/Container'
import { Card } from '../ui/Card'
import { MetricCounter } from '../ui/MetricCounter'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import type { Pillar } from '../../types'

const iconMap: Record<string, React.ElementType> = {
  build: Hammer,
  govern: ShieldCheck,
  knowledge: BrainCircuit,
  team: UsersRound,
}

export function SystemPillars() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const pillars: Pillar[] = t('systemPillars.items', { returnObjects: true }) as Pillar[]

  return (
    <section id="sistema" className="py-24">
      <Container>
        <div className="mb-16 text-center">
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t('systemPillars.label')}
          </span>
          <h2 className="mt-4 text-3xl font-semibold text-text-primary md:text-4xl">
            {t('systemPillars.title')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-text-secondary">
            {t('systemPillars.description')}
          </p>
        </div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="grid gap-6 lg:grid-cols-2"
        >
          {pillars.map((pillar, index) => {
            const Icon = iconMap[pillar.id] || BrainCircuit
            return (
              <Card key={pillar.id} variant="gradient" className="relative overflow-hidden">
                <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-accent/5 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent-light">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="text-sm font-medium text-text-muted">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                  </div>

                  <h3 className="mt-6 text-2xl font-semibold text-text-primary">{pillar.title}</h3>
                  <p className="mt-3 text-text-secondary">{pillar.description}</p>

                  <div className="mt-8 grid grid-cols-2 gap-4 border-t border-border pt-6">
                    {pillar.metrics.map((metric, metricIndex) => (
                      <MetricCounter
                        key={metricIndex}
                        value={metric.value}
                        suffix={metric.suffix}
                        label={metric.label}
                        decimals={metric.value % 1 !== 0 ? 1 : 0}
                      />
                    ))}
                  </div>
                </div>
              </Card>
            )
          })}
        </motion.div>
      </Container>
    </section>
  )
}
